import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiFacebook, FiInstagram, FiYoutube, FiMail, FiHeart } from 'react-icons/fi';

const FooterContainer = styled.footer`
  background: var(--neutral-gray-900);
  color: var(--neutral-gray-300);
  padding: var(--space-3xl) 0 var(--space-xl);
  margin-top: var(--space-3xl);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-md);
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-xl);
  margin-bottom: var(--space-xl);
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterSection = styled.div`
  h3 {
    font-family: var(--font-family-display);
    color: var(--neutral-white);
    font-size: 1.25rem;
    margin-bottom: var(--space-lg);
    font-weight: 600;
  }
`;

const FooterLink = styled(Link)`
  color: var(--neutral-gray-300);
  text-decoration: none;
  display: block;
  padding: var(--space-xs) 0;
  transition: var(--transition-base);
  
  &:hover {
    color: var(--primary-green-light);
  }
`;

const ExternalLink = styled.a`
  color: var(--neutral-gray-300);
  text-decoration: none;
  display: block;
  padding: var(--space-xs) 0;
  transition: var(--transition-base);
  
  &:hover {
    color: var(--primary-green-light);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--space-md);
  margin-top: var(--space-md);
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: var(--neutral-gray-800);
  border-radius: 50%;
  color: var(--neutral-gray-300);
  transition: var(--transition-base);
  
  &:hover {
    background: var(--primary-green);
    color: white;
    transform: translateY(-2px);
  }
`;

const Newsletter = styled.div`
  h3 {
    margin-bottom: var(--space-md);
  }
  
  p {
    margin-bottom: var(--space-lg);
    line-height: 1.6;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: var(--space-sm);
  margin-top: var(--space-md);
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: var(--space-md);
  border: 1px solid var(--neutral-gray-700);
  border-radius: var(--radius-lg);
  background: var(--neutral-gray-800);
  color: var(--neutral-white);
  font-size: 1rem;
  
  &::placeholder {
    color: var(--neutral-gray-400);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-green);
    box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
  }
`;

const NewsletterButton = styled.button`
  padding: var(--space-md) var(--space-lg);
  background: var(--primary-green);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition-base);
  
  &:hover {
    background: var(--primary-green-dark);
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid var(--neutral-gray-800);
  padding-top: var(--space-xl);
  text-align: center;
  
  @media (min-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
  }
`;

const Copyright = styled.p`
  margin: 0;
  color: var(--neutral-gray-400);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const FooterNav = styled.nav`
  display: flex;
  gap: var(--space-lg);
  margin-top: var(--space-md);
  justify-content: center;
  
  @media (min-width: 768px) {
    margin-top: 0;
    justify-content: flex-end;
  }
`;

const Footer = () => {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    const email = e.target.email.value;
    console.log('Newsletter subscription:', email);
    // Add actual newsletter logic here
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <h3>RV Better Together</h3>
            <p>
              Join Luke, Patty, and Dobby as they explore beautiful destinations
              across America in their home on wheels. Full-time RV living since 2023!
            </p>
            <SocialLinks>
              <SocialLink 
                href="https://facebook.com/rvbettertogether" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
              >
                <FiFacebook size={20} />
              </SocialLink>
              <SocialLink 
                href="https://instagram.com/rvbettertogether" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
              >
                <FiInstagram size={20} />
              </SocialLink>
              <SocialLink 
                href="https://youtube.com/rvbettertogether" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Subscribe to our YouTube channel"
              >
                <FiYoutube size={20} />
              </SocialLink>
              <SocialLink 
                href="mailto:hello@rvbettertogether.com"
                aria-label="Send us an email"
              >
                <FiMail size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>Explore</h3>
            <FooterLink to="/">Home</FooterLink>
            <FooterLink to="/about">About Us</FooterLink>
            <FooterLink to="/blog">Blog</FooterLink>
            <FooterLink to="/parks">Parks & Destinations</FooterLink>
            <FooterLink to="/gallery">Photo Gallery</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
          </FooterSection>

          <FooterSection>
            <h3>Resources</h3>
            <ExternalLink href="#" target="_blank">RV Maintenance Tips</ExternalLink>
            <ExternalLink href="#" target="_blank">Campground Reviews</ExternalLink>
            <ExternalLink href="#" target="_blank">Travel Itineraries</ExternalLink>
            <ExternalLink href="#" target="_blank">Gear Recommendations</ExternalLink>
            <ExternalLink href="#" target="_blank">Budget Planning</ExternalLink>
            <FooterLink to="/login">Premium Content</FooterLink>
          </FooterSection>

          <Newsletter>
            <h3>Stay Connected</h3>
            <p>
              Get the latest updates on our adventures, new blog posts, 
              and exclusive RV tips delivered to your inbox!
            </p>
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <NewsletterInput
                type="email"
                name="email"
                placeholder="Enter your email"
                required
              />
              <NewsletterButton type="submit">
                Subscribe
              </NewsletterButton>
            </NewsletterForm>
          </Newsletter>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            Made with <FiHeart size={16} color="#FF6B35" /> by RV Better Together © 2025
          </Copyright>
          <FooterNav>
            <ExternalLink href="/privacy">Privacy Policy</ExternalLink>
            <ExternalLink href="/terms">Terms of Service</ExternalLink>
          </FooterNav>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
